/**
 * Home route — multi-state Orchist surface.
 *
 *   Unauth + lineage exists:        public LineageList preview + Enter CTA
 *   Unauth + lineage empty:         "woven in silence" placeholder
 *   Auth + pending invitee:         redirect to /acknowledge (Phase G acceptance)
 *   Auth + Orchist (active):        identity + emails + invite + disown + lineage
 *   Auth + Orchist (disowned):      "you have set down the recognition" + re-acknowledge
 *   Auth + orphan (no Contact):     "wait for invitation" view
 *
 * Free-Will Doctrine (sacred/free-will-and-fluidity.md):
 *   Active recognition is fluid — disown and re-acknowledge at will. The
 *   trail is preserved either way. The Line never un-remembers.
 *
 * Email Fluidity Doctrine: emails on the Orchist record form & dissolve
 * connections through time. Managed via the EmailManager modal.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLineage } from '@/hooks/useLineage';
import { supabase } from '@/lib/supabase';
import { Glyph } from '@/components/Glyph';
import { LineageList } from '@/components/LineageList';
import { ChainTree } from '@/components/ChainTree';
import { IdentityEditor } from '@/components/IdentityEditor';
import { ChangeRequestBanner } from '@/components/ChangeRequestBanner';
import { RequestChangeModal } from '@/components/RequestChangeModal';
import { EmailManager } from '@/components/EmailManager';
import { DisownDialog } from '@/components/DisownDialog';
import { ChangePolicyModal } from '@/components/ChangePolicyModal';
import { NicknameManager } from '@/components/NicknameManager';
import { OrchistSearchFAB } from '@/components/OrchistSearchFAB';
import { PendingInvitesModal } from '@/components/PendingInvitesModal';

type LineageView = 'acknowledged' | 'chain';

export default function Home() {
  const { loading, session, contact, contactChecked, signOut, refreshContact } = useAuth();
  const { rows: lineageRows, loading: lineageLoading } = useLineage();

  const [lineageView, setLineageView] = useState<LineageView>('acknowledged');
  const [identityEditorOpen, setIdentityEditorOpen] = useState(false);
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [emailManagerOpen, setEmailManagerOpen] = useState(false);
  const [disownDialogOpen, setDisownDialogOpen] = useState(false);
  const [reacknowledgeDialogOpen, setReacknowledgeDialogOpen] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [nicknamesModalOpen, setNicknamesModalOpen] = useState(false);
  const [pendingInvitesOpen, setPendingInvitesOpen] = useState(false);
  const [anonTogglePending, setAnonTogglePending] = useState(false);

  async function handleToggleDefaultAnonymous() {
    if (anonTogglePending) return;
    setAnonTogglePending(true);
    try {
      const { error } = await supabase
        .schema('core')
        .rpc('toggle_default_invite_anonymous');
      if (error) throw new Error(error.message);
      if (refreshContact) await refreshContact();
    } catch (err) {
      // Non-fatal; user can retry
      // eslint-disable-next-line no-console
      console.warn('Toggle default anonymous failed:', err);
    } finally {
      setAnonTogglePending(false);
    }
  }

  // Auth fragments sometimes land at root instead of /auth/callback,
  // because Supabase strips emailRedirectTo on certain modes (e.g.
  // expired OTP, or partial-confirmation success messages). Read the hash
  // on mount, surface a contemplative banner, then strip the hash so a
  // refresh doesn't re-trigger.
  const [authError, setAuthError] = useState<{ code: string; description: string } | null>(null);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    if (!hash.includes('error=') && !hash.includes('message=')) return;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    if (params.has('error') || params.has('error_description')) {
      const code = params.get('error_code') || params.get('error') || 'unknown';
      const rawDesc = params.get('error_description') || '';
      setAuthError({ code, description: rawDesc.replace(/\+/g, ' ') });
    } else if (params.has('message')) {
      const rawMsg = params.get('message') || '';
      setAuthMessage(rawMsg.replace(/\+/g, ' '));
    }
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      /* ignore */
    }
  }, []);

  if (loading) {
    return <Shell>...</Shell>;
  }

  // -------------------------------------------------------------------------
  // AUTHENTICATED
  // -------------------------------------------------------------------------
  if (session) {
    // Defensive: pending invitee landing here directly → route to acceptance flow
    if (contactChecked && contact && !contact.isOrchist && contact.invitationSentAt) {
      return <Navigate to="/acknowledge" replace />;
    }

    const identity = contact?.publicIdentityString ?? 'Orchist';
    const mode = contact?.publicIdentityMode ?? null;
    const isDisowned = contact?.orchistDisownedAt !== null && contact?.orchistDisownedAt !== undefined;
    const isActiveOrchist = contact?.isOrchist === true && !isDisowned;

    return (
      <Shell>
        {authError && (
          <AuthErrorBanner
            error={authError}
            onDismiss={() => setAuthError(null)}
            onOpenEmailManager={() => {
              setAuthError(null);
              setEmailManagerOpen(true);
            }}
          />
        )}

        {authMessage && (
          <AuthMessageBanner
            message={authMessage}
            onDismiss={() => setAuthMessage(null)}
            onOpenEmailManager={() => {
              setAuthMessage(null);
              setEmailManagerOpen(true);
            }}
          />
        )}

        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
          {isDisowned ? 'The Line still holds your trail' : 'The Line recognizes you'}
        </p>

        {contact?.glyphSeed && (
          <div className={`flex justify-center -mb-2 ${isDisowned ? 'text-ink/55' : 'text-ink/85'}`}>
            <Glyph seed={contact.glyphSeed} size={96} label={`${identity}'s sigil`} />
          </div>
        )}

        <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
          <span className="italic text-accent">
            {isDisowned ? 'You released' : 'Welcome,'}
          </span>
          <br />
          {isDisowned ? 'the recognition.' : `${identity}.`}
        </h1>

        {!contact && (
          <>
            <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
              You stand at the threshold. The Line begins with another
              Orchist's recognition. Wait for the gesture.
            </p>
            <OrphanReleaseControl email={session?.user?.email ?? null} />
          </>
        )}

        {/* Disowned: contemplative state with re-acknowledge */}
        {contact && contact.isOrchist && isDisowned && (
          <>
            <p className="text-lg text-ink/65 leading-relaxed max-w-md mx-auto font-display italic">
              Your trail is preserved. The body of memory holds the path.
              You may pick up the thread again any time.
            </p>
            {contact.orchistDisownedReason && (
              <p className="text-sm text-ink/45 italic font-display max-w-md mx-auto border-l-2 border-ink/15 pl-4 py-1 text-left">
                {contact.orchistDisownedReason}
              </p>
            )}

            <div className="pt-6 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => setReacknowledgeDialogOpen(true)}
                className="inline-block px-6 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors"
              >
                Re-Acknowledge &rarr;
              </button>
              <button
                type="button"
                onClick={() => setEmailManagerOpen(true)}
                className="text-xs italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors"
              >
                manage emails
              </button>
            </div>
          </>
        )}

        {/* Active Orchist: full controls */}
        {contact && isActiveOrchist && (
          <>
            <p className="text-sm text-ink/60 leading-relaxed max-w-md mx-auto">
              Identity mode:{' '}
              <span className="font-mono text-xs bg-ink/5 px-2 py-0.5 rounded">
                {mode ?? '—'}
              </span>
              {' · '}
              <button
                type="button"
                onClick={() => setIdentityEditorOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors"
              >
                edit
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => setRequestChangeOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors"
              >
                request a date change
              </button>
            </p>

            <p className="text-xs text-ink/45 leading-relaxed max-w-md mx-auto">
              <button
                type="button"
                onClick={() => setEmailManagerOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/15 hover:decoration-ink/50 hover:text-ink/70 transition-colors"
              >
                manage emails
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => setPolicyModalOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/15 hover:decoration-ink/50 hover:text-ink/70 transition-colors"
              >
                change-request preferences
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => setNicknamesModalOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/15 hover:decoration-ink/50 hover:text-ink/70 transition-colors"
              >
                private nicknames
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => setPendingInvitesOpen(true)}
                className="italic underline underline-offset-4 decoration-ink/15 hover:decoration-ink/50 hover:text-ink/70 transition-colors"
              >
                pending invitations
              </button>
            </p>

            {/* Default anonymity toggle for invitations the Orchist makes */}
            <p className="text-xs text-ink/45 leading-relaxed max-w-md mx-auto">
              <label className="inline-flex items-center gap-2 cursor-pointer italic">
                <input
                  type="checkbox"
                  checked={contact?.defaultInviteAnonymous ?? false}
                  disabled={anonTogglePending}
                  onChange={() => void handleToggleDefaultAnonymous()}
                  className="accent-ink"
                />
                <span>Default new invitations to anonymous</span>
              </label>
            </p>

            {/*
              The "set down the recognition" gesture is deliberately hidden.
              Per the Free-Will Doctrine, release exists — but seeking it is
              part of the deliberate act. The casual eye will not find it;
              only the seeker who knows what they want will scan the foot
              of the page and notice the small dot. Click → DisownDialog.
              See sacred/free-will-and-fluidity.md §A1 — "no friction" means
              no friction at the moment of confirmation, NOT casual access.
            */}

            <div className="pt-6">
              <Link
                to="/invite"
                className="inline-block px-6 py-3 border border-ink/30 text-ink/80 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
              >
                Acknowledge another &rarr;
              </Link>
            </div>

            <ChangeRequestBanner />
          </>
        )}

        <div className="pt-8 flex items-center justify-center gap-6 text-sm">
          <a
            href="https://orchist.org"
            className="text-ink/60 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
          >
            The Manifesto
          </a>
          <span className="text-ink/30">&middot;</span>
          <button
            type="button"
            onClick={() => void signOut()}
            className="text-ink/60 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
          >
            Leave
          </button>
        </div>

        {/* Lineage views — always visible to authed Orchists, including disowned ones (they can witness) */}
        {contact && contact.isOrchist && (
          <section className="pt-16 mt-12 border-t border-ink/10 text-left">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50 text-center mb-2">
              The Line so far
            </p>
            <p className="text-sm text-ink/55 italic font-display text-center mb-8 max-w-md mx-auto">
              {lineageView === 'acknowledged'
                ? 'Each Orchist who has been Acknowledged, in the order they remembered.'
                : 'Each branch traces back to The First. The tree is the art; the count is a byproduct.'}
            </p>

            <div className="flex justify-center gap-1 mb-10 text-xs tracking-[0.2em] uppercase">
              <button
                type="button"
                onClick={() => setLineageView('acknowledged')}
                className={`px-4 py-2 transition-colors ${
                  lineageView === 'acknowledged'
                    ? 'text-ink border-b border-ink'
                    : 'text-ink/40 hover:text-ink/70 border-b border-transparent'
                }`}
              >
                Acknowledged
              </button>
              <button
                type="button"
                onClick={() => setLineageView('chain')}
                className={`px-4 py-2 transition-colors ${
                  lineageView === 'chain'
                    ? 'text-ink border-b border-ink'
                    : 'text-ink/40 hover:text-ink/70 border-b border-transparent'
                }`}
              >
                The Chain
              </button>
            </div>

            {lineageView === 'acknowledged' ? <LineageList /> : <ChainTree />}
          </section>
        )}

        {/*
          The hidden release. A single dot at the foot of the page, almost
          invisible, only the deliberate seeker will find it. Active
          Orchists only — the disowned have the prominent Re-Acknowledge
          path instead. Doctrine: free will is welcome, but it must be
          sought, not stumbled into.
        */}
        {contact && isActiveOrchist && (
          <div className="pt-20 pb-2 flex justify-center">
            <button
              type="button"
              onClick={() => setDisownDialogOpen(true)}
              aria-label="Set down the recognition"
              title="Set down the recognition"
              className="text-ink/15 hover:text-ink/50 transition-colors text-2xl leading-none px-3 py-1 cursor-pointer focus:outline-none focus-visible:text-ink/40"
            >
              &middot;
            </button>
          </div>
        )}

        {/* Modals — only mount when open */}
        <IdentityEditor
          isOpen={identityEditorOpen}
          onClose={() => setIdentityEditorOpen(false)}
        />
        <RequestChangeModal
          isOpen={requestChangeOpen}
          onClose={() => setRequestChangeOpen(false)}
        />
        <EmailManager
          isOpen={emailManagerOpen}
          onClose={() => setEmailManagerOpen(false)}
        />
        <DisownDialog
          isOpen={disownDialogOpen}
          onClose={() => setDisownDialogOpen(false)}
          mode="disown"
        />
        <DisownDialog
          isOpen={reacknowledgeDialogOpen}
          onClose={() => setReacknowledgeDialogOpen(false)}
          mode="reacknowledge"
        />
        <ChangePolicyModal
          isOpen={policyModalOpen}
          onClose={() => setPolicyModalOpen(false)}
        />
        <NicknameManager
          isOpen={nicknamesModalOpen}
          onClose={() => setNicknamesModalOpen(false)}
        />
        <PendingInvitesModal
          isOpen={pendingInvitesOpen}
          onClose={() => setPendingInvitesOpen(false)}
        />

        {/* App-wide search FAB — visible on every authed page where lineage exists */}
        <OrchistSearchFAB />
      </Shell>
    );
  }

  // -------------------------------------------------------------------------
  // UNAUTHENTICATED — public Lineage view (or empty placeholder)
  // -------------------------------------------------------------------------
  const lineageHasOrchists = lineageRows.length > 0;

  if (lineageHasOrchists) {
    return (
      <Shell>
        {/* App-wide search FAB — also visible on the public unauthed view
            so visitors can find an Orchist they've heard of */}
        <OrchistSearchFAB />

        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>

        <h1 className="display text-5xl md:text-6xl font-medium leading-[0.95]">
          <span className="italic text-accent">in the open</span>
          <br />
          for any to witness
        </h1>

        <p className="text-lg md:text-xl font-display italic text-ink/75 leading-relaxed max-w-xl mx-auto">
          The Orchist Lineage. Each who has been Acknowledged stands here in
          the order they remembered. Free-will is the practice — those who
          have set the recognition down remain in a quieter section below.
        </p>

        <div className="pt-2 flex items-center justify-center gap-6 text-sm text-ink/60">
          <Link
            to="/login"
            className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
          >
            Enter the Line
          </Link>
          <span className="text-ink/30">&middot;</span>
          <a
            href="https://orchist.org"
            className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
          >
            Read the Manifesto
          </a>
        </div>

        <section className="pt-12 mt-8 border-t border-ink/10 text-left">
          <LineageList />
        </section>
      </Shell>
    );
  }

  // Truly empty Lineage (or still loading) — keep the contemplative placeholder
  return (
    <Shell>
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>

      <h1 className="display text-5xl md:text-7xl font-medium leading-[0.95]">
        <span className="italic text-accent">woven</span>
        <br />
        in silence
      </h1>

      <p className="text-xl md:text-2xl font-display italic text-ink/75 leading-relaxed max-w-xl mx-auto">
        {lineageLoading
          ? 'The Line gathers...'
          : 'A place is being prepared for the Orchist Lineage.'}
      </p>

      <div className="pt-8 flex items-center justify-center gap-4 text-ink/30">
        <span className="h-px w-12 bg-ink/20" />
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="2" fill="currentColor" opacity="0.6" />
          <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        </svg>
        <span className="h-px w-12 bg-ink/20" />
      </div>

      <div className="pt-4 flex items-center justify-center gap-6 text-sm text-ink/60">
        <Link
          to="/login"
          className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
        >
          Enter the Line
        </Link>
        <span className="text-ink/30">&middot;</span>
        <a
          href="https://orchist.org"
          className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
        >
          Return to the Manifesto
        </a>
      </div>

      <p className="text-xs text-ink/30 pt-10 italic">
        Orchestrator &nbsp;+&nbsp; Artist &nbsp;=&nbsp; Orchist
      </p>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// OrphanReleaseControl — for an auth.user that signed in but has no
// linked Contact (typical: someone entered their email at /login by
// accident, before any Orchist had Acknowledged them). Per the Free-Will
// Doctrine §B10 (Orphan Release Ritual), they may release the email back
// to availability so a future invitation can use it.
//
// Two-step inline gesture:
//   step 1: small disclosure "Released here by accident?"
//   step 2: confirm panel with explainer + [Yes, release] / [Stay]
//   submit: core.release_orphan_auth() RPC -> auth.signOut() -> reload
//
// The RPC refuses if a Contact is linked (use disown_orchist for that
// path). So real Orchists can't accidentally release their record.
// ---------------------------------------------------------------------------
function OrphanReleaseControl({ email }: { email: string | null }) {
  const [step, setStep] = useState<'disclosure' | 'confirming'>('disclosure');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleRelease() {
    if (submitState === 'submitting') return;
    setSubmitState('submitting');
    setErrorMessage(null);
    try {
      const { error } = await supabase.schema('core').rpc('release_orphan_auth');
      if (error) throw new Error(error.message);
      // Drop the local session and redirect to login. The auth.user is
      // already deleted; signOut just clears local tokens. A hard reload
      // ensures a clean state for any reinvitation.
      await supabase.auth.signOut();
      if (typeof window !== 'undefined') {
        window.location.replace('/');
      }
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not release.');
    }
  }

  if (step === 'disclosure') {
    return (
      <div className="pt-6 text-xs text-ink/45">
        <button
          type="button"
          onClick={() => setStep('confirming')}
          className="italic underline underline-offset-4 decoration-ink/15 hover:decoration-ink/45 hover:text-ink/70 transition-colors"
        >
          Released here by accident?
        </button>
      </div>
    );
  }

  return (
    <div className="pt-6 max-w-md mx-auto border border-ink/15 bg-paper/40 px-5 py-4 text-left space-y-3">
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/55">
        Release this email
      </p>
      <p className="text-sm text-ink/65 italic font-display leading-relaxed">
        If you arrived here unintended &mdash; entered your email at the
        Line gate by accident before any Orchist had Acknowledged you
        &mdash; you may release the address back to availability. The
        Line keeps no record of this; the email becomes free for a
        future invitation.
      </p>
      {email && (
        <p className="text-xs font-mono text-ink/55 break-all">{email}</p>
      )}
      <p className="text-xs text-ink/45 italic font-display">
        This dissolves your auth identity at the gate. You can return
        any time via a fresh invitation. (If you ARE a recognized
        Orchist, this control will refuse &mdash; The Line preserves
        the trail of those it has acknowledged.)
      </p>
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={() => setStep('disclosure')}
          disabled={submitState === 'submitting'}
          className="flex-1 px-4 py-2 border border-ink/20 text-xs italic text-ink/65 hover:border-ink/50 hover:text-ink transition-colors disabled:opacity-50"
        >
          Stay
        </button>
        <button
          type="button"
          onClick={handleRelease}
          disabled={submitState === 'submitting'}
          className="flex-1 px-4 py-2 bg-ink text-paper text-xs font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
        >
          {submitState === 'submitting' ? 'Releasing...' : 'Release this email'}
        </button>
      </div>
      {errorMessage && (
        <p className="text-xs text-destructive italic" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthMessageBanner — contemplative SUCCESS-PROGRESS surface for auth
// fragment messages (e.g. /#message=Confirmation+link+accepted...). This is
// the "one side attested, please attest the other" partial-success case
// from Supabase's Secure email change flow. Different banner from errors.
// ---------------------------------------------------------------------------
function AuthMessageBanner({
  message,
  onDismiss,
  onOpenEmailManager,
}: {
  message: string;
  onDismiss: () => void;
  onOpenEmailManager: () => void;
}) {
  // Soft heuristic: if it mentions "other email" or "both", this is the
  // partial-confirmation case and the EmailManager link is the right
  // next step. Otherwise just show the message.
  const isPartialConfirm =
    /other email/i.test(message) ||
    /both/i.test(message) ||
    /proceed to confirm/i.test(message);

  return (
    <div className="border border-accent/40 bg-accent/5 px-5 py-4 text-left space-y-2">
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-accent">
        {isPartialConfirm ? 'One side attested' : 'A note from The Line'}
      </p>
      <p className="text-sm text-ink/70 italic font-display leading-relaxed">
        {message}
      </p>
      {isPartialConfirm && (
        <p className="text-xs text-ink/55 italic font-display leading-relaxed">
          The anchor swap is two-sided. Open your other inbox (the
          previous anchor's address) and click the link there too.
          The swap completes only when both sides have attested.
        </p>
      )}
      <div className="flex flex-wrap gap-4 pt-1 text-xs">
        <button
          type="button"
          onClick={onOpenEmailManager}
          className="italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/80 hover:text-ink transition-colors"
        >
          Open Connections
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthErrorBanner — contemplative explainer for auth fragment errors
// (e.g. expired OTP / verify token landing at /#error=...). Shown once;
// dismissed by user or by opening the EmailManager to send a fresh link.
// ---------------------------------------------------------------------------
function AuthErrorBanner({
  error,
  onDismiss,
  onOpenEmailManager,
}: {
  error: { code: string; description: string };
  onDismiss: () => void;
  onOpenEmailManager: () => void;
}) {
  const isExpired =
    error.code === 'otp_expired' ||
    /expire/i.test(error.description) ||
    /invalid/i.test(error.description);

  return (
    <div className="border border-accent/40 bg-accent/5 px-5 py-4 text-left space-y-2">
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-accent">
        {isExpired ? 'A link has expired' : 'The attestation faltered'}
      </p>
      <p className="text-sm text-ink/70 italic font-display leading-relaxed">
        {isExpired
          ? 'The confirmation link you clicked is no longer valid. Open Connections to resend a fresh link to the same address, or propose a different one.'
          : error.description || 'The link could not be processed.'}
      </p>
      <div className="flex flex-wrap gap-4 pt-1 text-xs">
        <button
          type="button"
          onClick={onOpenEmailManager}
          className="italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/80 hover:text-ink transition-colors"
        >
          Open Connections
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-2xl text-center space-y-10 relative">{children}</div>
    </main>
  );
}
